/* eslint-disable no-unused-vars */
import { useEffect, useRef, useState, useCallback } from "react";
import {
  getDrivers,
  updateDriverDetails,
  updateDriverStatus,
} from "../api/driverApi.js";
import DriverProfileModal from "../components/DriverProfileModal.jsx";
import { toast } from "react-toastify";
import {
  getAvatarColors,
  getInitials,
  getLast4Digits,
  renderStars,
} from "../utils/helper.js";
import Badges from "../components/Badges.jsx";
import { FiRefreshCw, FiWifi, FiWifiOff, FiAlertTriangle } from "react-icons/fi";
import Skeleton from "react-loading-skeleton";
import { socket } from "../socket.js";
import AccessControl from "../components/AccessControl.jsx";
import { getOrganization } from "../api/orgApi";
import { getZonesByOrg } from "../api/zoneApi";
import Loader from "../components/Loader.jsx";

const statusFilters = [
  "All",
  "approved",
  "applied",
  "verification_in_progress",
  "offline",
  "online",
  "waiting",
  "rejected",
];

// ✅ Short labels for filter tabs
const filterLabels = {
  verification_in_progress: "Verifying",
};

// ✅ Helper: Check if date is expired or expiring soon
const getExpiryStatus = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  const expiryDate = new Date(dateStr);
  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return { status: "expired", label: "Expired", color: "#A32D2D" };
  if (diffDays <= 30) return { status: "expiring", label: `${diffDays} days left`, color: "#854F0B" };
  return { status: "valid", label: "Valid", color: "#0F6E56" };
};

// ✅ Helper: Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function DriversPage() {
  const socketRef = useRef(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicleFilter, setVehicleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(socket.connected);
  const [zones, setZones] = useState([]);

  const getAllDrivers = useCallback(async () => {
    try {
      setLoading(true);

      const res = await getDrivers();

      if (res.success) {
        setDrivers(res.drivers);
      } else {
        toast.error(res.error || "Failed to fetch drivers");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllDrivers();
    // Fetch all zones (all orgs) for zone assignment in modal
    getOrganization().then((res) => {
      const orgs = res.orgs || [];
      Promise.all(orgs.map((org) => getZonesByOrg(org.orgId))).then((results) => {
        const allZones = results.flatMap((r) => r.zones || []);
        setZones(allZones);
      });
    });
  }, [getAllDrivers]);

  // 🔌 Socket connection
  useEffect(() => {
    socketRef.current = socket;

    const handleConnect = () => {
      console.log("🟢 Socket connected:", socket.id);
      setConnected(true);
      getAllDrivers();
    };

    const handleDisconnect = (reason) => {
      console.log("🔴 Socket disconnected:", reason);
      setConnected(false);
    };

    const handleDriverStatusChanged = (data) => {
      console.log("🔄 driver-status-changed:", data);

      setDrivers((prev) =>
        prev.map((d) =>
          d.driverId === data.driverId ? { ...d, status: data.status } : d,
        ),
      );

      setSelectedDriver((prev) =>
        prev?.driverId === data.driverId
          ? { ...prev, status: data.status }
          : prev,
      );

      const statusEmoji = {
        online: "🟢",
        offline: "🔴",
        approved: "✅",
        rejected: "❌",
        waiting: "🟡",
        applied: "📝",
        accepted: "🛺",
        confirmed: "🛺",
      };
      console.log(
        `${statusEmoji[data.status] || "📍"} Driver ${data.driverId} is now ${data.status}`,
      );
    };

    const handleDriversListAll = (payload) => {
      console.log("drivers-list-all received:", payload);

      if (payload.drivers && Array.isArray(payload.drivers)) {
        setDrivers((prevDrivers) => {
          const driverMap = new Map(
            prevDrivers.map((d) => [d.driverId, d]),
          );

          payload.drivers.forEach((socketDriver) => {
            const existing = driverMap.get(socketDriver.driverId);
            if (existing) {
              driverMap.set(socketDriver.driverId, {
                ...existing,
                status: socketDriver.status,
                latitude: socketDriver.latitude,
                longitude: socketDriver.longitude,
              });
            }
          });

          return Array.from(driverMap.values());
        });
      }
    };

    const handleDriverLocation = (data) => {
      console.log("driver-location:", data);
      setDrivers((prev) =>
        prev.map((d) =>
          d.driverId === data.driverId
            ? {
              ...d,
              latitude: data.latitude,
              longitude: data.longitude,
              ...(data.status ? { status: data.status } : {}),
            }
            : d,
        ),
      );
    };

    const handleNewDriverRegistered = (newDriver) => {
      console.log("🆕 new-driver-registered:", newDriver);

      setDrivers((prev) => {
        const exists = prev.some((d) => d.driverId === newDriver.driverId);
        if (exists) return prev;

        return [newDriver, ...prev];
      });

      toast.info(`🆕 New driver registered: ${newDriver.name || newDriver.phone}`, {
        autoClose: 5000,
      });
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("driver-status-changed", handleDriverStatusChanged);
    socket.on("drivers-list-all", handleDriversListAll);
    socket.on("driver-location", handleDriverLocation);
    socket.on("new-driver-registered", handleNewDriverRegistered);

    const refetchEvents = [
      "ride-accepted",
      "ride-declined",
      "ride-cancelled",
      "trip_completed",
    ];
    refetchEvents.forEach((evt) => socket.on(evt, getAllDrivers));

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("driver-status-changed", handleDriverStatusChanged);
      socket.off("drivers-list-all", handleDriversListAll);
      socket.off("driver-location", handleDriverLocation);
      socket.off("new-driver-registered", handleNewDriverRegistered);
      refetchEvents.forEach((evt) => socket.off(evt, getAllDrivers));
    };
  }, [getAllDrivers]);

  const handleStatusChange = async (driverId, newStatus) => {
    const previousDrivers = [...drivers];
    setDrivers((prev) =>
      prev.map((d) =>
        d.driverId === driverId ? { ...d, status: newStatus } : d,
      ),
    );

    const res = await updateDriverStatus(driverId, newStatus);
    if (res.success) {
      setDrivers((prev) =>
        prev.map((d) => (d.driverId === driverId ? res.driver : d)),
      );
      setSelectedDriver(null);
      toast.success(`Driver status updated to ${newStatus}`);
    } else {
      setDrivers(previousDrivers);
      toast.error(res.error || "Failed to update status");
    }
  };

  const handleUpdateDetails = async (driverId, payload) => {
    const res = await updateDriverDetails(driverId, payload);
    if (res.success) {
      setDrivers((prev) =>
        prev.map((d) => (d.driverId === driverId ? res.driver : d)),
      );
      toast.success("Driver details updated successfully!");
      setSelectedDriver(null);
      getAllDrivers();
    } else {
      toast.error(res.error || "Failed to update details");
    }
  };

  // ✅ NEW: Handle View Profile click - auto update "applied" to "verification_in_progress"
  const handleViewProfile = async (driver) => {
    if (driver.status === "applied") {
      // Update status to verification_in_progress
      const res = await updateDriverStatus(driver.driverId, "verification_in_progress");
      if (res.success) {
        // Update local state
        const updatedDriver = res.driver;
        setDrivers((prev) =>
          prev.map((d) => (d.driverId === driver.driverId ? updatedDriver : d)),
        );
        setSelectedDriver(updatedDriver);
        toast.info(`📋 ${driver.name} - Verification started`);
      } else {
        // If API fails, still open modal with original driver
        setSelectedDriver(driver);
        toast.error(res.error || "Failed to update status");
      }
    } else {
      // For other statuses, just open modal
      setSelectedDriver(driver);
    }
  };

  const filtered = drivers.filter((d) => {
    const matchVehicle = vehicleFilter === "All" || d.type === vehicleFilter;
    const matchStatus = statusFilter === "All" || d.status === statusFilter;
    const matchSearch =
      search === "" ||
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.vehicleNumber.toLowerCase().includes(search.toLowerCase());
    return matchVehicle && matchStatus && matchSearch;
  });

  const appliedDrivers = drivers.filter((d) => d.status === "applied");

  const stats = {
    total: drivers.length,
    approved: drivers.filter((d) => d.status === "approved").length,
    offline: drivers.filter((d) => d.status === "offline").length,
    online: drivers.filter((d) => d.status === "online").length,
    rejected: drivers.filter((d) => d.status === "rejected").length,
    waiting: drivers.filter((d) => d.status === "waiting").length,
    applied: appliedDrivers.length,
    verification_in_progress: drivers.filter((d) => d.status === "verification_in_progress").length,
  };

  // ✅ Check if driver has all required documents for approval
  const canApproveDriver = (d) => {
    return (
      d.rcBookCopy &&
      d.rcBookCopy !== "" &&
      d.insuranceCopy &&
      d.insuranceCopy !== "" &&
      d.photo &&
      d.photo !== "" &&
      d.drivingLicenceFrontCopy &&
      d.drivingLicenceFrontCopy !== "" &&
      d.drivingLicenceBackCopy &&
      d.drivingLicenceBackCopy !== ""
    );
  };

  // ✅ Check if any document is expired
  const hasExpiredDocuments = (d) => {
    const insuranceStatus = getExpiryStatus(d.insuranceEndDate);
    const dlStatus = getExpiryStatus(d.drivingLicenceValidUpto);

    return (
      (insuranceStatus && insuranceStatus.status === "expired") ||
      (dlStatus && dlStatus.status === "expired")
    );
  };

  // ✅ Check if any document is expiring soon (within 30 days)
  const hasExpiringDocuments = (d) => {
    const insuranceStatus = getExpiryStatus(d.insuranceEndDate);
    const dlStatus = getExpiryStatus(d.drivingLicenceValidUpto);

    return (
      (insuranceStatus && insuranceStatus.status === "expiring") ||
      (dlStatus && dlStatus.status === "expiring")
    );
  };

  return (
    <div>
      <AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN", "HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />
      {loading && (
        <Loader />
      )}

      {/* ✅ Connection Status Banner */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          padding: "8px 16px",
          borderRadius: 8,
          background: connected ? "#E1F5EE" : "#FCEBEB",
          color: connected ? "#0F6E56" : "#A32D2D",
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {connected ? (
          <>
            <FiWifi size={16} />
            <span>Live updates actives</span>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#0F6E56",
                animation: "pulse 2s infinite",
              }}
            />
          </>
        ) : (
          <>
            <FiWifiOff size={16} />
            <span>Disconnected - Updates paused</span>
          </>
        )}
      </div>

      {/* Summary */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-label">Total Drivers</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Approved</div>
          <div className="stat-value" style={{ color: "#0F6E56" }}>
            {stats.approved}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Offline</div>
          <div className="stat-value" style={{ color: "#854F0B" }}>
            {stats.offline}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Online</div>
          <div
            className="stat-value"
            style={{
              color: "#00c73fff",
              transition: "all 0.3s ease",
            }}
          >
            {stats.online}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rejected</div>
          <div className="stat-value" style={{ color: "#A32D2D" }}>
            {stats.rejected}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Waiting</div>
          <div className="stat-value" style={{ color: "#d0237cff" }}>
            {stats.waiting}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Applied</div>
          <div className="stat-value" style={{ color: "#0077cc" }}>
            {stats.applied}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Verification</div>
          <div className="stat-value" style={{ color: "#7C3AED" }}>
            {stats.verification_in_progress}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {statusFilters.map((s) => {
              const count =
                s === "All"
                  ? drivers.length
                  : drivers.filter((d) => d.status === s).length;

              return (
                <button
                  key={s}
                  className={`filter-tab ${statusFilter === s ? "active" : ""}`}
                  onClick={() => setStatusFilter(s)}
                  style={{ position: "relative" }}
                >
                  {filterLabels[s] || s}
                  <span
                    style={{
                      marginLeft: 4,
                      fontSize: 11,
                      opacity: 0.7,
                    }}
                  >
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="search-wrap">
          <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            className="search-input"
            placeholder="Search drivers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button onClick={getAllDrivers} className="btn-outline-green btn-sm">
          <FiRefreshCw
            style={{
              marginRight: 6,
              animation: loading ? "spin 1s linear infinite" : "none",
            }}
          />
        </button>
      </div>

      {/* All Drivers Section */}
      <h2 style={{ marginTop: 30 }}>All Drivers</h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="driver-card">
              <div className="driver-card-header">
                <Skeleton circle width={44} height={44} />
                <div style={{ flex: 1, marginLeft: 12 }}>
                  <Skeleton width={120} height={16} />
                  <Skeleton width={80} height={14} style={{ marginTop: 4 }} />
                </div>
                <Skeleton width={40} height={20} />
              </div>

              <div className="driver-stats">
                <Skeleton width={60} height={18} />
                <Skeleton width={80} height={18} />
                <Skeleton width={50} height={18} />
              </div>

              <div style={{ marginTop: 12 }}>
                <Skeleton width="100%" height={30} />
              </div>
            </div>
          ))
          : filtered.map((d) => {
            const colors = getAvatarColors(d.name);
            const phoneNumber = getLast4Digits(d.phone);
            const insuranceExpiry = getExpiryStatus(d.insuranceEndDate);
            const dlExpiry = getExpiryStatus(d.drivingLicenceValidUpto);
            const isExpired = hasExpiredDocuments(d);
            const isExpiring = hasExpiringDocuments(d);

            return (
              <div
                key={d.driverId}
                className={
                  d.status === "applied"
                    ? "applied-driver-card"
                    : "driver-card"
                }
                style={{
                  transition: "all 0.3s ease",
                  // ✅ Highlight if documents expired
                  ...(isExpired && {
                    borderColor: "#A32D2D",
                    borderWidth: 2,
                  }),
                  ...(isExpiring && !isExpired && {
                    borderColor: "#854F0B",
                    borderWidth: 2,
                  }),
                }}
              >
                <div className="driver-card-header">
                  <div
                    className="avatar"
                    style={{
                      width: 44,
                      height: 44,
                      background: colors.backgroundColor,
                      color: colors.color,
                      fontSize: 14,
                      borderRadius: "50%",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {d.photo ? (
                      <img
                        src={d.photo}
                        alt={d.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      getInitials(d.name)
                    )}
                    {d.status === "online" && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          background: "#00c73f",
                          border: "2px solid white",
                          animation: "pulse 2s infinite",
                        }}
                      />
                    )}
                  </div>

                  <div className="driver-card-info">
                    <div className="driver-name">
                      {d.name}
                      {/* ✅ Warning icon if documents expired/expiring */}
                      {(isExpired || isExpiring) && (
                        <FiAlertTriangle
                          size={14}
                          style={{
                            marginLeft: 6,
                            color: isExpired ? "#A32D2D" : "#854F0B",
                          }}
                          title={isExpired ? "Documents expired" : "Documents expiring soon"}
                        />
                      )}
                    </div>
                    <div>{renderStars(d.avgRating)} </div>
                    <div className="driver-sub">
                      {d.vehicleNumber} · {d.type}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <Badges status={d.status} />
                  </div>
                </div>

                {/* ✅ Document Expiry Info */}
                {(d.insuranceEndDate || d.drivingLicenceValidUpto) && (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginTop: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {d.insuranceEndDate && insuranceExpiry && (
                      <div
                        style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: insuranceExpiry.status === "expired" ? "#FCEBEB" :
                            insuranceExpiry.status === "expiring" ? "#FEF3CD" : "#E1F5EE",
                          color: insuranceExpiry.color,
                        }}
                      >
                        🛡️ Ins: {formatDate(d.insuranceEndDate)}
                      </div>
                    )}
                    {d.drivingLicenceValidUpto && dlExpiry && (
                      <div
                        style={{
                          fontSize: 11,
                          padding: "2px 8px",
                          borderRadius: 4,
                          background: dlExpiry.status === "expired" ? "#FCEBEB" :
                            dlExpiry.status === "expiring" ? "#FEF3CD" : "#E1F5EE",
                          color: dlExpiry.color,
                        }}
                      >
                        🪪 DL: {formatDate(d.drivingLicenceValidUpto)}
                      </div>
                    )}
                  </div>
                )}

                <div className="driver-stats">
                  <div className="driver-stat-item">
                    <div className="driver-stat-val">0</div>
                    <div className="driver-stat-lbl">Rides Today</div>
                  </div>
                  <div className="driver-stat-item">
                    <div className="driver-stat-val">₹ 0</div>
                    <div className="driver-stat-lbl">Earned</div>
                  </div>
                  <div className="driver-stat-item">
                    <div className="driver-stat-val">{phoneNumber}</div>
                    <div className="driver-stat-lbl">Phone (last 4)</div>
                  </div>
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button
                    className="btn-outline-green btn-sm"
                    style={{ flex: 1 }}
                    onClick={() => handleViewProfile(d)}
                  >
                    View Profile
                  </button>

                  {(d.status !== "applied" && d.status !== "rejected") ? (
                    <button
                      className="btn btn-sm"
                      style={{
                        flex: 1,
                        background: "#FCEBEB",
                        color: "#A32D2D",
                      }}
                      onClick={() =>
                        handleStatusChange(d.driverId, "rejected")
                      }
                    >
                      Reject
                    </button>
                  ) : d.status === "rejected" ? (
                    <button
                      className="btn btn-sm"
                      style={{
                        flex: 1,
                        background: "#E1F5EE",
                        color: "#0F6E56",
                      }}
                      onClick={() =>
                        handleStatusChange(d.driverId, "approved")
                      }
                    >
                      Reinstate
                    </button>
                  ) : d.status === "waiting" && canApproveDriver(d) ? (
                    <button
                      className="btn btn-sm"
                      style={{
                        flex: 1,
                        background: "#E1F5EE",
                        color: "#0F6E56",
                      }}
                      onClick={() =>
                        handleStatusChange(d.driverId, "approved")
                      }
                    >
                      Approve
                    </button>
                  ) : d.status === "waiting" && !canApproveDriver(d) ? (
                    <button
                      className="btn btn-sm"
                      style={{
                        flex: 1,
                        background: "#FEF3CD",
                        color: "#854F0B",
                        cursor: "not-allowed",
                      }}
                      disabled
                      title="Missing required documents (Photo, RC, Insurance, DL Front & Back)"
                    >
                      Docs Missing
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
      </div>

      {/* Profile Modal */}
      {selectedDriver && (
        <DriverProfileModal
          driver={selectedDriver}
          onClose={() => setSelectedDriver(null)}
          onUpdateStatus={handleStatusChange}
          onUpdateDetails={handleUpdateDetails}
          zones={zones}
        />
      )}

      {/* ✅ Add pulse animation CSS */}
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.6;
              transform: scale(1.1);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
}